import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { observer } from "mobx-react-lite";
import { AUTH_ROLES } from "@/constants/auth";
import useStore from "@/hooks/useStore";
import { getDefaultRoute } from "@/routes/loaders";
import { getPayload as getImpersonationPayload } from "@/impersonation/impersonationSession";

import {
  navigationItems,
  getFooterItems,
  CRM_NAV_ITEM_ID,
  isExternalNavItem,
  isAbsoluteHttpUrl,
  isAllowedExternalNavUrl,
} from "./constants";
import { getAvailableNavUrlsForRole } from "./navRoleAccess";
import "./InteractiveSidebar.css";
import SidebarHeader from "./SidebarHeader";
import SidebarNavigation from "./SidebarNavigation";
import SidebarFooter from "./SidebarFooter";
import AccountSwitcherModal from "./AccountSwitcherModal";
import ActingUserPickerModal from "./ActingUserPickerModal";

const InteractiveSidebar = observer(
  ({
    isMobile = false,
    onItemClick,
    onCustomerChange,
    onActingUserChange,
    handleSignOut,
    onCollapseChange,
    contentHeight = null,
    navigationRoleOverride = "",
  }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authStore, actingUserDirectoryStore } = useStore();
    const currentCustomer = authStore?.customer;
    const accounts = Array.isArray(authStore?.accounts) ? authStore.accounts : [];
    /** Default landing page per role: homeowner → /properties, investor → /opportunities, else → /unified-email-inbox. */
    const defaultLandingPath = getDefaultRoute(authStore);
    /** SECURITY: Picker capability follows real Cognito groups, not effective impersonated role. */
    const canSwitchCustomer = Boolean(authStore?.cognitoIsSuperAdmin);

    const navRole = String(navigationRoleOverride || authStore?.role || "")
      .trim()
      .toLowerCase();

    const filteredNavigationItems = useMemo(() => {
      const availableUrls = getAvailableNavUrlsForRole(navRole);
      if (availableUrls === null) {
        return navigationItems;
      }

      const allowExternalCrm = navRole === AUTH_ROLES.REALTOR;
      return navigationItems.filter((item) => {
        if (availableUrls.has(item.url)) return true;
        return allowExternalCrm && item.id === CRM_NAV_ITEM_ID && item.external === true;
      });
    }, [navRole]);

    const sidebarFooterItems = useMemo(() => getFooterItems(), []);

    // SECURITY: Memoizing to prevent infinite re-render loops from reference changes
    // ASSUMPTION: accounts array contents are stable between renders when unchanged
    const availableAccounts = useMemo(() => {
      return accounts && accounts.length > 0
        ? accounts.map((acc) => {
            const name = authStore?.getDisplayName?.(acc) ?? "Unknown";
            return {
              id: acc.id ?? acc.name,
              name,
              email: acc.email ?? `${name?.toLowerCase() ?? "user"}@horaizen.com`,
              initial: (name ?? "U").charAt(0).toUpperCase(),
            };
          })
        : [];
    }, [accounts, authStore]);

    /** Logged-in user's account for display; never default to platform account (e.g. horAIzen). */
    const currentUserAccount = useMemo(() => {
      const match = availableAccounts.find((acc) => acc.id === currentCustomer);
      if (match) return match;
      const name = authStore?.getDisplayName?.() ?? "User";
      const email = authStore?.getEffectiveEmail?.() ?? "user@horaizen.com";
      return {
        id: currentCustomer ?? "",
        name: name !== "" ? name : "User",
        email: email !== "" ? email : "user@horaizen.com",
        initial: (name !== "" ? name : "U").charAt(0).toUpperCase(),
      };
    }, [availableAccounts, currentCustomer, authStore]);

    /** Hide duplicated account selector below the top header. */
    const showCustomerSwitcherInSidebar = false;

    const [isCollapsed, setIsCollapsed] = useState(() => {
      if (isMobile) return false;
      const saved = localStorage.getItem("sidebarCollapsed");
      return saved === "true";
    });

    const [selectedAccount, setSelectedAccount] = useState(null);
    const [isAccountSwitcherOpen, setIsAccountSwitcherOpen] = useState(false);
    const [isActingUserPickerOpen, setIsActingUserPickerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [hasNavOverflow, setHasNavOverflow] = useState(false);
    const navScrollRegionRef = useRef(null);
    const overflowRafRef = useRef(0);

    useEffect(() => {
      if (onCollapseChange && !isMobile) {
        onCollapseChange(isCollapsed);
      }
    }, [isCollapsed, onCollapseChange, isMobile]);

    useEffect(() => {
      if (!isMobile) {
        localStorage.setItem("sidebarCollapsed", isCollapsed.toString());
      }
    }, [isCollapsed, isMobile]);

    const measureOverflow = useCallback(() => {
      const region = navScrollRegionRef.current;
      if (!region) return;
      const needsScroll = region.scrollHeight > region.clientHeight + 1;
      setHasNavOverflow((previous) => (previous === needsScroll ? previous : needsScroll));
    }, []);

    const scheduleOverflowMeasure = useCallback(() => {
      if (overflowRafRef.current) {
        window.cancelAnimationFrame(overflowRafRef.current);
      }
      overflowRafRef.current = window.requestAnimationFrame(() => {
        overflowRafRef.current = 0;
        measureOverflow();
      });
    }, [measureOverflow]);

    useLayoutEffect(() => {
      scheduleOverflowMeasure();
      return () => {
        if (overflowRafRef.current) {
          window.cancelAnimationFrame(overflowRafRef.current);
          overflowRafRef.current = 0;
        }
      };
    }, [scheduleOverflowMeasure, isCollapsed, isMobile, location.pathname, filteredNavigationItems]);

    useEffect(() => {
      const region = navScrollRegionRef.current;
      if (!region) return undefined;

      const onWindowResize = () => {
        scheduleOverflowMeasure();
      };

      let resizeObserver;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          scheduleOverflowMeasure();
        });
        resizeObserver.observe(region);
        if (region.firstElementChild) {
          resizeObserver.observe(region.firstElementChild);
        }
      }

      window.addEventListener("resize", onWindowResize);
      scheduleOverflowMeasure();

      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        window.removeEventListener("resize", onWindowResize);
      };
    }, [scheduleOverflowMeasure, isCollapsed, isMobile, location.pathname, filteredNavigationItems]);

    /** Background warm cache for acting-user picker (getAllUsers); does not block paint. */
    useEffect(() => {
      if (!canSwitchCustomer || !onActingUserChange) return undefined;
      const cid = String(currentCustomer ?? "").trim();
      if (!cid) return undefined;
      actingUserDirectoryStore.prefetchForCustomer(cid);
      return undefined;
    }, [canSwitchCustomer, onActingUserChange, currentCustomer, actingUserDirectoryStore]);

    const handleToggleCollapse = () => {
      setIsCollapsed((prev) => {
        const newState = !prev;
        if (newState) {
          setIsAccountSwitcherOpen(false);
          setIsActingUserPickerOpen(false);
        }
        return newState;
      });
    };

    /**
     * Determines if a navigation item should be marked as active based on the current pathname.
     * SECURITY: Handles routes with detail pages (e.g., /properties/:id, /campaigns/:id) by checking
     * if the pathname matches the base route or starts with the base route followed by a path segment.
     * ASSUMPTION: Detail page routes follow the pattern /baseRoute/:id
     *
     * @param {{ url?: string, external?: boolean } | string} itemOrUrl - Nav item (preferred) or legacy URL string
     * @param {string} pathname - The current location pathname
     * @returns {boolean} True if the item should be active, false otherwise
     */
    const isNavigationItemActive = React.useCallback((itemOrUrl, pathname) => {
      const item = typeof itemOrUrl === "string" ? { url: itemOrUrl } : itemOrUrl;
      if (isExternalNavItem(item)) {
        return false;
      }
      const itemUrl = item?.url;
      switch (itemUrl) {
        case "/properties":
          return pathname === "/properties" || pathname.startsWith("/properties/") || pathname.startsWith("/property/");
        case "/campaigns":
          return pathname === "/campaigns" || pathname.startsWith("/campaigns/");
        case "/opportunities":
          return pathname === "/opportunities" || pathname.startsWith("/opportunities/");
        default:
          return pathname === itemUrl;
      }
    }, []);

    const sidebarNavigationItems = useMemo(() => {
      return filteredNavigationItems.map((item) => ({
        ...item,
        isActive: isNavigationItemActive(item, location.pathname),
      }));
    }, [filteredNavigationItems, isNavigationItemActive, location.pathname]);

    const handleItemClick = (item) => {
      if (item.url === "/sign-out") {
        if (handleSignOut) {
          handleSignOut();
        }
      } else if (isExternalNavItem(item)) {
        if (!isAllowedExternalNavUrl(item.url)) {
          return;
        }
      } else if (isAbsoluteHttpUrl(item.url)) {
        return;
      } else {
        navigate(item.url);
      }
      if (onItemClick) {
        onItemClick();
      }
    };

    const handleAccountSelect = (account) => {
      setSelectedAccount(account);
      if (onCustomerChange) {
        onCustomerChange(account.id);
      }
      setIsAccountSwitcherOpen(false);
      setSearchQuery(""); // Clear search when account is selected
    };

    const filteredAccounts = useMemo(() => {
      if (!searchQuery.trim()) {
        return availableAccounts;
      }

      const query = searchQuery.toLowerCase().trim();
      return availableAccounts.filter((account) => {
        const nameMatch = account.name?.toLowerCase().includes(query);
        const emailMatch = account.email?.toLowerCase().includes(query);
        return nameMatch || emailMatch;
      });
    }, [availableAccounts, searchQuery]);

    // Sync selected account with logged-in user / store customer so "User" shows correct account on load.
    useEffect(() => {
      if (currentUserAccount) {
        setSelectedAccount(currentUserAccount);
      }
    }, [currentUserAccount]);

    const displayAccount = canSwitchCustomer ? selectedAccount || currentUserAccount : currentUserAccount;

    /** Signed-in superadmin card for the acting-user modal (“Current user”); not the impersonation target. */
    const superadminUserCard = useMemo(() => {
      const payload = getImpersonationPayload();
      const name = String(authStore?.cognitoUserId ?? "").trim() || "User";
      const email = String(payload?.realEmail ?? authStore?.getSignedInEmail?.() ?? "").trim();
      return {
        name: name !== "" ? name : "User",
        email: email !== "" ? email : "user@horaizen.com",
        initial: (name !== "" ? name : "U").charAt(0).toUpperCase(),
      };
    }, [authStore]);

    const handleStopActingAsUser = () => {
      if (onActingUserChange) {
        onActingUserChange(null);
      }
    };

    const handleActingUserPicked = (selection) => {
      if (onActingUserChange) {
        onActingUserChange(selection);
      }
    };

    return (
      <div
        className={`interactive-sidebar ${isMobile ? "mobile" : "desktop"} ${isCollapsed && !isMobile ? "collapsed" : ""}`}
        style={{
          ...(contentHeight && !isMobile
            ? {
                height: `${contentHeight}px`,
                maxHeight: `${contentHeight}px`,
              }
            : {}),
        }}
      >
        <SidebarHeader
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          canSwitchCustomer={canSwitchCustomer}
          showCustomerSwitcher={showCustomerSwitcherInSidebar}
          isAccountSwitcherOpen={isAccountSwitcherOpen}
          setIsAccountSwitcherOpen={setIsAccountSwitcherOpen}
          displayAccount={displayAccount}
          defaultLandingPath={defaultLandingPath}
          onNavigateHome={() => navigate(defaultLandingPath)}
          onActingUserChange={onActingUserChange}
          isActingUserPickerOpen={isActingUserPickerOpen}
          setIsActingUserPickerOpen={setIsActingUserPickerOpen}
          onStopActingAsUser={handleStopActingAsUser}
          onToggleCollapse={handleToggleCollapse}
        />

        <div
          ref={navScrollRegionRef}
          className={`sidebar-scroll-region app-scrollbar ${hasNavOverflow ? "has-overflow" : ""} ${
            isCollapsed && !isMobile ? "collapsed" : ""
          }`}
        >
          <SidebarNavigation
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            filteredNavigationItems={sidebarNavigationItems}
            location={location}
            handleItemClick={handleItemClick}
            isNavigationItemActive={isNavigationItemActive}
          />
        </div>

        <SidebarFooter
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          footerItems={sidebarFooterItems}
          handleItemClick={handleItemClick}
        />

        {canSwitchCustomer && showCustomerSwitcherInSidebar && (
          <AccountSwitcherModal
            isAccountSwitcherOpen={isAccountSwitcherOpen}
            setIsAccountSwitcherOpen={setIsAccountSwitcherOpen}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            displayAccount={displayAccount}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredAccounts={filteredAccounts}
            handleAccountSelect={handleAccountSelect}
          />
        )}

        {canSwitchCustomer && onActingUserChange && (
          <ActingUserPickerModal
            isOpen={isActingUserPickerOpen}
            onClose={() => setIsActingUserPickerOpen(false)}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            customerId={String(currentCustomer ?? "").trim()}
            signedInUserCard={superadminUserCard}
            onStopActing={handleStopActingAsUser}
            onSelect={handleActingUserPicked}
          />
        )}
      </div>
    );
  }
);

export default InteractiveSidebar;
